import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { BadgeCheck } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { fetchVerified, type AdminVerifiedUser } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { Tag } from '@/components/StatusBadge'

/**
 * A9.12 — Verifikatsiyalanganlar reyestri. GET /admin/verified (backend `663ef50`,
 * users.is_official). Ariza tasdiqlanганда user shu ro'yxatga tushadi.
 */
export function VerifiedRegistry() {
  const t = useT()

  const columns = useMemo<AdminColumn<AdminVerifiedUser>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 80 },
      {
        key: 'username',
        header: t.col.name,
        type: 'text',
        minWidth: 200,
        sortable: false,
        render: (r) => (
          <span className="flex items-center gap-1.5">
            <Link to={`/users/${r.id}`} className="text-accent hover:underline">
              {r.first_name || (r.username ? `@${r.username}` : `#${r.id}`)}
            </Link>
            {r.is_bot && <Tag>bot</Tag>}
          </span>
        ),
      },
      { key: 'username', header: t.col.username, type: 'text', width: 160, render: (r) => (r.username ? `@${r.username}` : '—') },
      { key: 'created_at', header: t.col.registered, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchVerified, [])

  return (
    <>
      <PageHeader
        title={t.verif.registry}
        screenId="A9.12"
        actions={
          <Link
            to="/verification"
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
          >
            {t.verif.queue}
          </Link>
        }
      />
      <DataTable<AdminVerifiedUser>
        screenId="A9.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <BadgeCheck size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
